// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector } from '../../../store/hooks/hooks'
import { useReadOnlyMode } from '../../../store/hooks/useReadOnlyMode'
import {
    selectActiveSelection,
    selectActivePendingInitialSelectionId,
    selectActiveSelectedNodeIds,
} from '../../../store/selectors/selectionSelectors'
import {
    selectActiveChatInput,
    selectActiveChatMessages,
    selectIsActiveChatProcessing,
} from '../../../store/selectors/chatSelectors'
import { selectActiveSession, selectActiveSessionId } from '../../../store/selectors/sessionSelectors'
import { selectLorasState } from '../../../store/slices/lorasSlice'
import {
    fetchAgentSuggestions,
    type AgentSuggestion,
} from '../../../services/agent'

export interface RecommendationState {
    recommendations: AgentSuggestion[]
    isLoading: boolean
    isManualRefreshing: boolean
    hasStaleContext: boolean
    refreshRecommendations: () => void
}

const EMPTY_RECOMMENDATIONS: AgentSuggestion[] = []
const STATIC_FALLBACK_RECOMMENDATIONS: AgentSuggestion[] = []
const EMPTY_SELECTED_NODE_IDS: string[] = []
const DEBOUNCE_MS = 500

export function useRecommendations(): RecommendationState {
    const readOnly = useReadOnlyMode()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const input = useAppSelector(selectActiveChatInput)
    const messages = useAppSelector(selectActiveChatMessages)
    const isProcessing = useAppSelector(selectIsActiveChatProcessing)
    const selectedNodeIds = useAppSelector(selectActiveSelectedNodeIds)
    const pendingInitialSelectionId = useAppSelector(selectActivePendingInitialSelectionId)
    const selection = useAppSelector(selectActiveSelection)
    const loras = useAppSelector(selectLorasState)

    const selectedLoraId = useMemo(() => {
        if (!selection.brushEnabled || !selection.hasMaskStrokes) return null
        return loras.selectedId || null
    }, [selection.brushEnabled, selection.hasMaskStrokes, loras.selectedId])

    const lastAssistantMessage = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            const message = messages[i]
            if (message.kind === 'bubble' && message.role === 'assistant') {
                return message
            }
        }
        return null
    }, [messages])

    const lastAssistantMessageId = lastAssistantMessage?.id ?? null
    const lastAssistantPending = Boolean(lastAssistantMessage?.pending)

    const [recommendations, setRecommendations] = useState<AgentSuggestion[]>(
        EMPTY_RECOMMENDATIONS
    )
    const [isLoading, setIsLoading] = useState(false)
    const [isManualRefreshing, setIsManualRefreshing] = useState(false)
    const [refreshToken, setRefreshToken] = useState(0)

    const debounceRef = useRef<number | null>(null)
    const lastRequestKeyRef = useRef<string | null>(null)
    const abortRef = useRef<AbortController | null>(null)
    const prevIsProcessingRef = useRef(isProcessing)
    const manualRefreshRequestedRef = useRef(false)
    const inputRef = useRef(input)
    const selectedNodeIdsRef = useRef(selectedNodeIds)
    const selectedLoraIdRef = useRef(selectedLoraId)
    const lastFetchSignatureRef = useRef<string | null>(null)
    const lastAssistantMessageIdRef = useRef<string | null>(lastAssistantMessageId)

    useEffect(() => {
        inputRef.current = input
    }, [input])

    const effectiveSelectedNodeIds = useMemo(() => {
        if (selectedNodeIds.length > 0) return selectedNodeIds
        if (pendingInitialSelectionId) return [pendingInitialSelectionId]
        return EMPTY_SELECTED_NODE_IDS
    }, [pendingInitialSelectionId, selectedNodeIds])

    useEffect(() => {
        selectedNodeIdsRef.current = effectiveSelectedNodeIds
    }, [effectiveSelectedNodeIds])

    useEffect(() => {
        selectedLoraIdRef.current = selectedLoraId
    }, [selectedLoraId])

    const contextSignature = useMemo(() => {
        return JSON.stringify({
            inputText: input.trim(),
            selectedNodeIds: effectiveSelectedNodeIds,
            selectedLoraId,
        })
    }, [input, effectiveSelectedNodeIds, selectedLoraId])

    const hasStaleContext =
        lastFetchSignatureRef.current !== null &&
        lastFetchSignatureRef.current !== contextSignature

    const refreshRecommendations = useCallback(() => {
        manualRefreshRequestedRef.current = true
        setRefreshToken((prev) => prev + 1)
    }, [])

    useEffect(() => {
        if (
            lastAssistantMessageId &&
            lastAssistantMessageId !== lastAssistantMessageIdRef.current
        ) {
            setRecommendations(EMPTY_RECOMMENDATIONS)
        }
        lastAssistantMessageIdRef.current = lastAssistantMessageId
    }, [lastAssistantMessageId])

    useEffect(() => {
        if (isProcessing && !prevIsProcessingRef.current) {
            abortRef.current?.abort()
            setIsLoading(false)
            setRecommendations(EMPTY_RECOMMENDATIONS)
        }
        prevIsProcessingRef.current = isProcessing
    }, [isProcessing])

    useEffect(() => {
        if (readOnly || !activeSessionId || !session?.id) {
            setRecommendations(EMPTY_RECOMMENDATIONS)
            return
        }

        const inputText = inputRef.current.trim()
        const latestSelectedNodeIds = selectedNodeIdsRef.current
        const latestSelectedLoraId = selectedLoraIdRef.current
        const hasPromptContext =
            Boolean(inputText) || latestSelectedNodeIds.length > 0 || Boolean(latestSelectedLoraId)

        if (messages.length === 0 && !hasPromptContext) {
            setRecommendations(EMPTY_RECOMMENDATIONS)
            lastFetchSignatureRef.current = contextSignature
            return
        }

        const hasConversation = messages.length > 0
        if (isProcessing || lastAssistantPending || (hasConversation && !lastAssistantMessageId)) {
            return
        }

        const key = JSON.stringify({
            sessionId: activeSessionId,
            lastAssistantMessageId: hasConversation ? lastAssistantMessageId : null,
            refreshToken,
            contextSignature,
        })
        if (key === lastRequestKeyRef.current) return

        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current)
        }

        debounceRef.current = window.setTimeout(() => {
            lastRequestKeyRef.current = key
            abortRef.current?.abort()
            const controller = new AbortController()
            abortRef.current = controller
            const isManualRequest = manualRefreshRequestedRef.current
            manualRefreshRequestedRef.current = false
            setIsLoading(true)
            if (isManualRequest) {
                setIsManualRefreshing(true)
            }

            const capturedSignature = JSON.stringify({
                inputText,
                selectedNodeIds: latestSelectedNodeIds,
                selectedLoraId: latestSelectedLoraId,
            })

            fetchAgentSuggestions(
                {
                    session_id: activeSessionId,
                    input_text: inputText || null,
                    selected_nodes:
                        latestSelectedNodeIds.length > 0 ? latestSelectedNodeIds : null,
                    selected_lora_id: latestSelectedLoraId,
                },
                controller.signal
            )
                .then((response) => {
                    setRecommendations(response.recommendations ?? EMPTY_RECOMMENDATIONS)
                    lastFetchSignatureRef.current = capturedSignature
                })
                .catch(() => {
                    if (!inputText && latestSelectedNodeIds.length === 0) {
                        setRecommendations(STATIC_FALLBACK_RECOMMENDATIONS)
                    } else {
                        setRecommendations(EMPTY_RECOMMENDATIONS)
                    }
                    lastFetchSignatureRef.current = capturedSignature
                })
                .finally(() => {
                    setIsLoading(false)
                    if (isManualRequest) {
                        setIsManualRefreshing(false)
                    }
                })
        }, DEBOUNCE_MS)

        return () => {
            if (debounceRef.current) {
                window.clearTimeout(debounceRef.current)
            }
            abortRef.current?.abort()
        }
    }, [
        readOnly,
        activeSessionId,
        session?.id,
        messages.length,
        isProcessing,
        lastAssistantMessageId,
        lastAssistantPending,
        refreshToken,
        contextSignature,
    ])

    return {
        recommendations,
        isLoading,
        isManualRefreshing,
        hasStaleContext,
        refreshRecommendations,
    }
}
